import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface User {
  id: string;
  email: string;
  name: string;
  role: 'episcop' | 'vicar' | 'paroh' | 'secretar' | 'contabil';
  parohieId?: string;
  permissions: string[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock users pentru demonstrație
const mockUsers: User[] = [
  {
    id: '1',
    email: 'episcop@episcopie.ro',
    name: 'Episcop',
    role: 'episcop',
    permissions: ['all']
  },
  {
    id: '2',
    email: 'vicar@episcopie.ro',
    name: 'Vicar',
    role: 'vicar',
    permissions: ['registratura.read', 'registratura.write', 'gestiune.read', 'concedii.read', 'concedii.write', 'utilizatori.read', 'parohii.read', 'parohii.write', 'gestiuni.read', 'gestiuni.write', 'contracte.read', 'contracte.write']
  },
  {
    id: '3',
    email: 'paroh@parohia1.ro',
    name: 'Paroh 1',
    role: 'paroh',
    parohieId: 'parohia1',
    permissions: ['registratura.read', 'gestiune.read', 'gestiune.write', 'concedii.read', 'concedii.write', 'parohii.read', 'gestiuni.read', 'contracte.read', 'contracte.write']
  },
  {
    id: '4',
    email: 'secretar@episcopie.ro',
    name: 'Secretar',
    role: 'secretar',
    permissions: ['registratura.read', 'registratura.write', 'concedii.read', 'parohii.read', 'contracte.read']
  },
  {
    id: '5',
    email: 'contabil@episcopie.ro',
    name: 'Contabil',
    role: 'contabil',
    permissions: ['gestiune.read', 'gestiune.write', 'contracte.read']
  }
];

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    // Verifică dacă există un utilizator salvat în localStorage
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      setUser(JSON.parse(savedUser));
    }
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulare autentificare
    const foundUser = mockUsers.find(u => u.email === email);
    if (foundUser && password === 'password123') {
      setUser(foundUser);
      localStorage.setItem('user', JSON.stringify(foundUser));
      return true;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    if (user.permissions.includes('all')) return true;
    return user.permissions.includes(permission);
  };

  const hasRole = (role: string): boolean => {
    if (!user) return false;
    return user.role === role;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    login,
    logout,
    hasPermission,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
